import { useEffect, useMemo, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import {
  ExternalLink,
  Filter,
  X,
  Scale,
  MapPin,
  Factory,
} from "lucide-react";
import { api } from "@/lib/api";
import SEO, { collectionSchema } from "@/components/SEO";

const STATUS_TAG = {
  Active: "bb-tag-positive",
  Pilot: "bb-tag-neutral",
  "In Review": "bb-tag-neutral",
  "Decision Pending": "bb-tag-negative",
};

const formatDate = (iso) =>
  new Date(iso).toLocaleDateString("en-CA", {
    month: "long",
    day: "numeric",
    year: "numeric",
  });

const monthKey = (iso) => {
  const d = new Date(iso);
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`;
};

const monthLabel = (iso) =>
  new Date(iso).toLocaleDateString("en-CA", {
    month: "long",
    year: "numeric",
  });

const Chip = ({ label, active, onClick, testId, accent = "navy" }) => {
  const base =
    "inline-flex items-center gap-1.5 px-3 py-1.5 text-[11px] font-archivo uppercase tracking-widest border transition-colors";
  const styles = active
    ? accent === "copper"
      ? "bg-bb-copper text-white border-bb-copper"
      : "bg-bb-navy text-white border-bb-navy"
    : "bg-white text-bb-navy border-border hover:border-bb-navy";
  return (
    <button
      type="button"
      onClick={onClick}
      className={`${base} ${styles}`}
      data-testid={testId}
      data-active={active}
    >
      {label}
      {active && <X size={11} strokeWidth={2.5} />}
    </button>
  );
};

export default function PolicyTrackerPage() {
  const [items, setItems] = useState([]);
  const [regions, setRegions] = useState([]);
  const [commodities, setCommodities] = useState([]);
  const [loading, setLoading] = useState(true);

  const [searchParams, setSearchParams] = useSearchParams();
  const jurisdiction = searchParams.get("jurisdiction") || null;
  const status = searchParams.get("status") || null;
  const region = searchParams.get("region") || null;
  const commodity = searchParams.get("commodity") || null;

  const updateParam = (key, next) => {
    const params = new URLSearchParams(searchParams);
    if (!next || params.get(key) === next) params.delete(key);
    else params.set(key, next);
    setSearchParams(params, { replace: false });
  };

  useEffect(() => {
    let mounted = true;
    Promise.all([
      api.get("/policy-items"),
      api.get("/regions"),
      api.get("/commodities"),
    ])
      .then(([p, r, c]) => {
        if (!mounted) return;
        if (Array.isArray(p.data)) setItems(p.data);
        if (Array.isArray(r.data)) setRegions(r.data);
        if (Array.isArray(c.data)) setCommodities(c.data);
      })
      .catch(() => {})
      .finally(() => mounted && setLoading(false));
    return () => {
      mounted = false;
    };
  }, []);

  const regionMap = useMemo(
    () => Object.fromEntries(regions.map((r) => [r.id, r])),
    [regions],
  );
  const commodityMap = useMemo(
    () => Object.fromEntries(commodities.map((c) => [c.id, c])),
    [commodities],
  );

  const jurisdictions = useMemo(() => {
    const set = new Set();
    items.forEach((i) => i.jurisdiction && set.add(i.jurisdiction));
    return Array.from(set).sort();
  }, [items]);

  const statuses = useMemo(() => {
    const set = new Set();
    items.forEach((i) => i.status && set.add(i.status));
    return Array.from(set).sort();
  }, [items]);

  const filtered = useMemo(() => {
    return items
      .filter((i) => {
        if (jurisdiction && i.jurisdiction !== jurisdiction) return false;
        if (status && i.status !== status) return false;
        if (region && i.relatedRegion !== region) return false;
        if (commodity && i.relatedCommodity !== commodity) return false;
        return true;
      })
      .sort((a, b) => (a.effectiveDate < b.effectiveDate ? 1 : -1));
  }, [items, jurisdiction, status, region, commodity]);

  const grouped = useMemo(() => {
    const map = {};
    filtered.forEach((i) => {
      const k = monthKey(i.effectiveDate);
      if (!map[k]) map[k] = { label: monthLabel(i.effectiveDate), items: [] };
      map[k].items.push(i);
    });
    return Object.entries(map).sort((a, b) => (a[0] < b[0] ? 1 : -1));
  }, [filtered]);

  const activeCount =
    (jurisdiction ? 1 : 0) +
    (status ? 1 : 0) +
    (region ? 1 : 0) +
    (commodity ? 1 : 0);

  const clearAll = () =>
    setSearchParams(new URLSearchParams(), { replace: false });

  return (
    <div data-testid="policy-tracker-page">
      <SEO
        title="Policy Tracker"
        description="A structured ledger of federal, provincial, territorial, and bilateral actions affecting Canadian mining — bills, regulations, court decisions, funding programs, and consultation orders."
        jsonLd={collectionSchema("Policy Tracker", "Canadian mining policy ledger", "/policy-tracker")}
      />
      {/* Header */}
      <section className="bg-white border-b border-border">
        <div className="bb-container py-12 lg:py-16">
          <div className="flex items-end justify-between flex-wrap gap-6">
            <div>
              <div className="bb-overline text-bb-copper">
                Policy · Ledger
              </div>
              <h1
                className="bb-headline mt-4 text-4xl sm:text-5xl lg:text-6xl text-bb-navy"
                data-testid="policy-page-headline"
              >
                Policy Tracker
              </h1>
              <p className="mt-5 text-base lg:text-lg text-bb-slate max-w-2xl leading-relaxed">
                A structured ledger of federal, provincial, territorial, and
                bilateral actions affecting Canadian mining — bills,
                regulations, court decisions, funding programs, and
                consultation orders.
              </p>
            </div>
            <div className="flex items-center gap-3">
              <span className="bb-overline text-bb-slate">
                {filtered.length} {filtered.length === 1 ? "file" : "files"}
              </span>
              {activeCount > 0 && (
                <button
                  type="button"
                  onClick={clearAll}
                  className="bb-btn-outline inline-flex items-center gap-2 px-4 py-2 text-xs"
                  data-testid="policy-clear-filters"
                >
                  <X size={14} strokeWidth={2.4} />
                  Clear filters ({activeCount})
                </button>
              )}
            </div>
          </div>
        </div>
      </section>

      {/* Filters */}
      <section
        className="bg-bb-offwhite border-b border-border sticky top-16 z-30"
        data-testid="policy-filter-bar"
      >
        <div className="bb-container py-5 space-y-3">
          <div className="flex items-start gap-4">
            <div className="flex items-center gap-2 min-w-[110px] pt-1.5">
              <Filter size={13} className="text-bb-slate" />
              <span className="bb-overline text-bb-slate">Jurisdiction</span>
            </div>
            <div className="flex flex-wrap gap-2">
              {jurisdictions.map((j) => (
                <Chip
                  key={j}
                  label={j}
                  active={jurisdiction === j}
                  onClick={() => updateParam("jurisdiction", j)}
                  testId={`chip-jurisdiction-${j.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
                  accent="copper"
                />
              ))}
            </div>
          </div>
          <div className="flex items-start gap-4">
            <div className="flex items-center gap-2 min-w-[110px] pt-1.5">
              <span className="bb-overline text-bb-slate">Status</span>
            </div>
            <div className="flex flex-wrap gap-2">
              {statuses.map((s) => (
                <Chip
                  key={s}
                  label={s}
                  active={status === s}
                  onClick={() => updateParam("status", s)}
                  testId={`chip-status-${s.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
                />
              ))}
            </div>
          </div>
          <div className="flex items-start gap-4">
            <div className="flex items-center gap-2 min-w-[110px] pt-1.5">
              <span className="bb-overline text-bb-slate">Region</span>
            </div>
            <div className="flex flex-wrap gap-2">
              {regions.map((r) => (
                <Chip
                  key={r.id}
                  label={r.name}
                  active={region === r.id}
                  onClick={() => updateParam("region", r.id)}
                  testId={`chip-region-${r.slug}`}
                />
              ))}
            </div>
          </div>
          <div className="flex items-start gap-4">
            <div className="flex items-center gap-2 min-w-[110px] pt-1.5">
              <span className="bb-overline text-bb-slate">Commodity</span>
            </div>
            <div className="flex flex-wrap gap-2">
              {commodities.map((c) => (
                <Chip
                  key={c.id}
                  label={c.name}
                  active={commodity === c.id}
                  onClick={() => updateParam("commodity", c.id)}
                  testId={`chip-commodity-${c.slug}`}
                />
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Timeline */}
      <section className="py-12 lg:py-16">
        <div className="bb-container">
          {loading ? (
            <div className="text-center py-20 bb-overline text-bb-slate">
              Loading policy ledger…
            </div>
          ) : filtered.length === 0 ? (
            <div
              className="border border-border bg-white p-12 text-center"
              data-testid="policy-empty-state"
            >
              <div className="bb-overline text-bb-copper">No results</div>
              <h3 className="font-archivo text-2xl text-bb-navy mt-3 font-semibold">
                No policy files match those filters.
              </h3>
              <button
                type="button"
                onClick={clearAll}
                className="bb-btn-primary inline-flex items-center justify-center gap-2 px-5 py-3 text-sm mt-6"
                data-testid="policy-empty-clear"
              >
                Clear all filters
              </button>
            </div>
          ) : (
            <div
              className="relative max-w-4xl mx-auto"
              data-testid="policy-timeline"
            >
              {/* Vertical rail */}
              <div className="absolute left-4 md:left-8 top-0 bottom-0 w-px bg-border" />

              {grouped.map(([key, group]) => (
                <div
                  key={key}
                  className="mb-10"
                  data-testid={`policy-month-${key}`}
                >
                  {/* Month heading */}
                  <div className="flex items-center gap-4 mb-6 relative">
                    <div className="w-8 md:w-16 flex justify-center">
                      <span
                        className="w-3 h-3 rounded-full bg-bb-navy relative z-10"
                        style={{ boxShadow: "0 0 0 4px #F8FAFC" }}
                      />
                    </div>
                    <h2 className="font-archivo text-xl text-bb-navy font-bold tracking-tight uppercase">
                      {group.label}
                    </h2>
                    <div className="flex-1 h-px bg-border" />
                    <span className="bb-overline text-bb-slate">
                      {group.items.length}{" "}
                      {group.items.length === 1 ? "item" : "items"}
                    </span>
                  </div>

                  {/* Items */}
                  <div className="space-y-5">
                    {group.items.map((item) => {
                      const statusCls =
                        STATUS_TAG[item.status] || "bb-tag-neutral";
                      const rel = regionMap[item.relatedRegion];
                      const com = commodityMap[item.relatedCommodity];
                      return (
                        <div
                          key={item.id}
                          className="flex gap-4 md:gap-6 relative"
                          data-testid={`policy-item-${item.id}`}
                        >
                          <div className="w-8 md:w-16 flex justify-center pt-3 relative shrink-0">
                            <span className="w-2 h-2 rounded-full bg-bb-copper relative z-10 ring-4 ring-bb-offwhite" />
                          </div>
                          <div className="flex-1 bb-card p-5 lg:p-6 flex flex-col gap-3">
                            <div className="flex items-center justify-between flex-wrap gap-2">
                              <div className="flex items-center gap-3 flex-wrap">
                                <span className="bb-overline text-bb-copper">
                                  {item.jurisdiction}
                                </span>
                                <span
                                  className={`text-[10px] font-mono px-2 py-1 ${statusCls}`}
                                >
                                  {item.status}
                                </span>
                              </div>
                              <span className="text-[11px] font-mono text-bb-slate">
                                {formatDate(item.effectiveDate)}
                              </span>
                            </div>

                            <h3 className="font-archivo text-lg lg:text-xl text-bb-navy font-semibold tracking-tight leading-snug">
                              {item.title}
                            </h3>
                            <div className="text-xs font-mono text-bb-slate">
                              {item.agency}
                            </div>
                            <p className="text-sm text-bb-slate leading-relaxed">
                              {item.summary}
                            </p>

                            <div className="mt-2 pt-4 border-t border-border flex items-center justify-between flex-wrap gap-3">
                              <div className="flex flex-wrap items-center gap-2">
                                {rel && (
                                  <Link
                                    to={`/regions/${rel.slug}`}
                                    className="inline-flex items-center gap-1.5 text-[11px] font-mono text-bb-navy bb-link"
                                    data-testid={`policy-item-${item.id}-region`}
                                  >
                                    <MapPin size={11} /> {rel.name}
                                  </Link>
                                )}
                                {com && (
                                  <Link
                                    to={`/commodities/${com.slug}`}
                                    className="inline-flex items-center gap-1.5 text-[11px] font-mono text-bb-navy bb-link"
                                    data-testid={`policy-item-${item.id}-commodity`}
                                  >
                                    <Factory size={11} /> {com.name}
                                  </Link>
                                )}
                              </div>
                              {item.sourceUrl && (
                                <a
                                  href={item.sourceUrl}
                                  target="_blank"
                                  rel="noreferrer"
                                  className="inline-flex items-center gap-1.5 text-[11px] font-archivo uppercase tracking-widest text-bb-navy bb-link"
                                  data-testid={`policy-item-${item.id}-source`}
                                >
                                  <ExternalLink size={11} />
                                  Source
                                </a>
                              )}
                            </div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              ))}

              {/* Bottom cap */}
              <div className="flex items-center gap-4 relative">
                <div className="w-8 md:w-16 flex justify-center">
                  <span className="w-3 h-3 bg-bb-slate/30 rotate-45 relative z-10" />
                </div>
                <span className="bb-overline text-bb-slate">
                  End of ledger · {filtered.length} total
                </span>
              </div>
            </div>
          )}
        </div>
      </section>

      {/* Bottom stats strip */}
      <section className="border-t border-border bg-white py-10">
        <div className="bb-container">
          <div className="flex items-center gap-4">
            <Scale size={20} className="text-bb-copper" />
            <p className="text-sm text-bb-slate font-mono leading-relaxed">
              The BedrockBulletin Policy Tracker aggregates public regulatory,
              legislative, and consultation actions relevant to Canadian
              mining. All entries are illustrative placeholder data for demo
              purposes and should not be relied upon for legal or compliance
              decisions.
            </p>
          </div>
        </div>
      </section>
    </div>
  );
}
